import { defineStore } from 'pinia'
import { reactive } from 'vue'

interface IStorePromises {
	fetchLocationsPromise: Promise<ILocationModel[]> | null
	fetchLocationCitiesPromise: Promise<ILocationCityModel[]> | null
}

export interface ILocationsStoreState {
	locations: ILocationModel[] | null
	cities: ILocationCityModel[] | null
	fetchingLocations: boolean
}

export interface ILocationsStore {
	readonly state: {
		locations: ILocationModel[] | null
		cities: ILocationCityModel[] | null
		fetchingLocations: boolean
	}
	setLocations: (locations: ILocationModel[]) => void
	fetchLocations: (server?: boolean) => Promise<ILocationModel[] | undefined>
	fetchLocationCities: (server?: boolean) => Promise<ILocationCityModel[] | undefined>
	setFetchingLocations: (fetching: boolean) => void
	getLocations: ComputedRef<ILocationModel[] | null>
	getFetchingLocations: ComputedRef<boolean>
	getIsFetchingLocationPromise: ComputedRef<boolean>
}

export const useLocationsStore = defineStore('locationsStore', (): ILocationsStore => {
	const _state = reactive<ILocationsStoreState>({
		locations: null,
		cities: null,
		fetchingLocations: false,
	})

	const promises: IStorePromises = {
		fetchLocationsPromise: null,
		fetchLocationCitiesPromise: null,
	}

	const setLocations = (locations: ILocationModel[]): void => {
		_state.locations = locations
	}

	const fetchLocationCities = async (server?: boolean) => {
		try {
			if (_state.cities && _state.cities.length > 0) return _state.cities

			if (promises.fetchLocationCitiesPromise) return promises.fetchLocationCitiesPromise

			promises.fetchLocationCitiesPromise = (async () => {
				const fetchedCities = await ApiService.fetchAllLocationCities(server)

				if (fetchedCities && fetchedCities.length > 0) {
					_state.cities = fetchedCities
				} else {
					_state.cities = []
				}

				return _state.cities as ILocationCityModel[]
			})()

			return await promises.fetchLocationCitiesPromise
		} catch (error) {
			console.log(error)
		}
	}

	const fetchLocations = async (server?: boolean) => {
		try {
			if (_state.locations && _state.locations.length > 0) {
				return _state.locations
			}

			if (promises.fetchLocationsPromise) {
				return promises.fetchLocationsPromise
			}

			promises.fetchLocationsPromise = (async () => {
				const { data } = await ApiService.fetchAllLocations(1, 100, undefined, true, server)

				if (data && data.length > 0) {
					setLocations(data)
				} else {
					setLocations([])
				}

				return _state.locations as ILocationModel[]
			})()

			return await promises.fetchLocationsPromise
		} catch (error) {
			console.log(error)
		} finally {
			promises.fetchLocationsPromise = null
		}
	}

	const setFetchingLocations = (fetching: boolean): void => {
		_state.fetchingLocations = fetching
	}

	const getLocations = computed(() => _state.locations)

	const getFetchingLocations = computed(() => _state.fetchingLocations)

	const getIsFetchingLocationPromise = computed(() => promises.fetchLocationsPromise !== null)

	return {
		state: _state,
		setLocations,
		fetchLocations,
		fetchLocationCities,
		setFetchingLocations,
		getLocations,
		getFetchingLocations,
		getIsFetchingLocationPromise,
	}
})
